/**
 * Lógica PURA del recolector de suscripciones de pozos de un sorteo.
 *
 * No toca el DOM ni importa `@shared`: el módulo del panel lee los controles y
 * le pasa valores crudos; acá se parsean, se validan y se arma el body que
 * viaja al backend. Así se prueba con `node:test` sin navegador, y los dos
 * caminos de guardado (botón del tab y Guardar del modal) comparten EXACTAMENTE
 * la misma autoridad.
 */

/**
 * Porcentaje de aporte de una suscripción, tal como lo escribe el operador.
 * Vacío = `null` = hereda el porcentaje por defecto del pozo (herencia
 * dinámica: si el defecto cambia, cambia el aporte). Un número = override fijo.
 * Un texto que no es número también es `null`: nunca un fallback inventado.
 */
export function parsearPorcentajeOverride(raw) {
    const texto = String(raw ?? '').trim();
    if (texto === '') return null;
    const numero = parseFloat(texto);
    return Number.isFinite(numero) && numero > 0 ? numero : null;
}

/** Pausa al disparar, en segundos; cualquier cosa que no sea entero >= 0 es 0. */
export function parsearPausaSegundos(raw) {
    const numero = parseInt(String(raw ?? '').trim(), 10);
    return Number.isInteger(numero) && numero >= 0 ? numero : 0;
}

/**
 * Body de suscripciones a partir de lo LEÍDO de los controles.
 *
 * @param {{ editorMultinivelActivo: boolean,
 *           slots: Array<{ nivel: string, pozoId: string|number|null, porcentaje: string, pausa: string }>,
 *           filas: Array<{ pozoId: number, marcada: boolean, porcentaje: string, pausa: string }> }} lectura
 *   `slots` es lo leído del editor multinivel (una entrada por posición, SIN
 *   porcentaje: el aporte lo define el pozo y la suscripción va con NULL);
 *   `filas` es lo leído de la lista del modo vigente (una por pozo disponible).
 *   Solo se usa la fuente del modo activo: el otro conjunto puede venir vacío.
 * @returns {{ suscripciones: Array, modoMultinivel: boolean }}
 */
export function construirBodyDeSuscripciones(lectura) {
    const editorMultinivelActivo = lectura?.editorMultinivelActivo === true;
    const fuente = editorMultinivelActivo ? lectura.slots || [] : lectura?.filas || [];
    const suscripciones = [];
    const yaIncluidos = new Set();
    for (const entrada of fuente) {
        const pozoId = editorMultinivelActivo ? Number(entrada.pozoId) : Number(entrada.pozoId);
        const entra = editorMultinivelActivo ? Boolean(entrada.pozoId) : entrada.marcada === true;
        if (!entra || !Number.isInteger(pozoId) || pozoId <= 0 || yaIncluidos.has(pozoId)) continue;
        yaIncluidos.add(pozoId);
        suscripciones.push({
            pozo_id: pozoId,
            // En multinivel el total y su reparto visible/reserva pertenecen al
            // pozo: un override por colección dejaría dinero sin destino, así que
            // la suscripción viaja siempre en NULL (el backend lo exige).
            porcentaje_aporte: editorMultinivelActivo ? null : parsearPorcentajeOverride(entrada.porcentaje),
            paralizar_segundos: parsearPausaSegundos(entrada.pausa),
        });
    }
    return { suscripciones, modoMultinivel: editorMultinivelActivo };
}
